class HeroSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.hero__slides');
    if (!this.track) return;
    this.slides = Array.from(this.track.children);
    this.dots = Array.from(this.querySelectorAll('.hero__dot'));
    if (this.slides.length < 2) return;

    this.index = 0;
    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.speed = Math.max(parseInt(this.dataset.speed, 10) || 5, 3) * 1000;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.timer = null;
    this.dragging = false;

    this.dots.forEach((dot, dotIndex) => {
      dot.addEventListener('click', () => {
        this.goTo(dotIndex);
        this.restartAutoplay();
      });
    });

    this.addEventListener('pointerenter', () => this.stopAutoplay());
    this.addEventListener('pointerleave', () => this.startAutoplay());
    this.addEventListener('focusin', () => this.stopAutoplay());
    this.addEventListener('focusout', () => this.startAutoplay());

    this.onVisibilityChange = () => {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    };
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.onMotionChange = () => {
      if (this.reducedMotion.matches) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    };
    this.reducedMotion.addEventListener('change', this.onMotionChange);

    this.track.addEventListener('pointerdown', (event) => this.onPointerDown(event));
    this.track.addEventListener('dragstart', (event) => event.preventDefault());

    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    if (this.onVisibilityChange) document.removeEventListener('visibilitychange', this.onVisibilityChange);
    if (this.onMotionChange) this.reducedMotion.removeEventListener('change', this.onMotionChange);
    this.removeDragListeners();
  }

  goTo(index) {
    const count = this.slides.length;
    this.index = ((index % count) + count) % count;
    this.track.style.transform = `translateX(-${this.index * 100}%)`;
    this.dots.forEach((dot, dotIndex) => {
      const active = dotIndex === this.index;
      dot.classList.toggle('is-active', active);
      if (active) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  startAutoplay() {
    if (!this.autoplayEnabled || this.reducedMotion.matches || document.hidden || this.dragging) return;
    this.stopAutoplay();
    this.timer = setInterval(() => this.goTo(this.index + 1), this.speed);
  }

  stopAutoplay() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }

  onPointerDown(event) {
    if (event.button !== undefined && event.button !== 0) return;
    this.dragging = true;
    this.startX = event.clientX;
    this.currentX = event.clientX;
    this.classList.add('is-dragging');
    this.track.style.transition = 'none';
    this.stopAutoplay();

    this.onPointerMove = (moveEvent) => {
      this.currentX = moveEvent.clientX;
      const delta = this.currentX - this.startX;
      this.track.style.transform = `translateX(calc(-${this.index * 100}% + ${delta}px))`;
    };

    this.onPointerUp = () => {
      const delta = this.currentX - this.startX;
      this.dragging = false;
      this.classList.remove('is-dragging');
      this.track.style.transition = '';
      this.removeDragListeners();
      if (Math.abs(delta) > this.track.offsetWidth * 0.15) {
        this.goTo(this.index + (delta < 0 ? 1 : -1));
      } else {
        this.goTo(this.index);
      }
      this.startAutoplay();
    };

    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);
  }

  removeDragListeners() {
    if (this.onPointerMove) window.removeEventListener('pointermove', this.onPointerMove);
    if (this.onPointerUp) {
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
    }
  }
}

customElements.define('hero-slider', HeroSlider);
